/**
 * FormAccess Page
 *
 * Step 3: Form Access & Identity Verification
 * Collects additional identity information and verifies against database records.
 * On success, navigates to PDF generation step,Password Protected and Email Delivery.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { useFormAccess } from '../hooks/useFormAccess';
import FormField from '../components/FormField/FormField';
import AlertBanner from '../components/AlertBanner/AlertBanner';
import pwdVisible from '../assets/images/Pwdvisible.png';
import pwdHide from '../assets/images/Pwdhide.png';
import '../assets/styles/FormAccess.css';

/**
 * Main component for Step 3 form access.
 *
 * Flow:
 * 1. Guard: Check sessionToken from Step 2
 * 2. Render form with fields: firstName, lastName?, ssn4, email?
 * 3. On submit: validate locally, then submit to backend
 * 4. On success: store formAccessId, navigate to PDF generation
 * 5. On error: display error banner, allow retry
 */
export default function FormAccess() {
  const navigate = useNavigate();
  const { sessionToken } = useSession();

  // Initialize form state management
  const form = useFormAccess(sessionToken || '');

  const [showDownloadBanner, setShowDownloadBanner] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    if (form.result?.downloaded) {
      setShowDownloadBanner(true);
      setConsentChecked(false);
      const timer = setTimeout(() => setShowDownloadBanner(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [form.result?.downloaded]);

  // ── Guard: Must have sessionToken from Step 2 ──────────────────────────
  useEffect(() => {
    if (!sessionToken) {
      navigate('/error?reason=NO_SESSION');
    }
  }, [sessionToken, navigate]);

  // ── Handle successful submission ────────────────────────────────────────
  // PDF is streamed directly from the backend — no navigation needed.
  // The download dialog fires inside formAccessApi.ts; we just show the banner here.

  // ── Handle session expired during submission ───────────────────────────
  useEffect(() => {
    if (form.result?.error?.code === 'SESSION_EXPIRED') {
      const timer = setTimeout(() => {
        navigate('/error?reason=SESSION_EXPIRED');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [form.result, navigate]);

  const handleSsn4Change = (value: string) => {
    form.setSsn4(value.replace(/\D/g, '').slice(0, 4));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.submit();
  };

  return (
    <div className="form-access-wrapper">
      <div className="form-access-card">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="form-access-header">
          <span className="form-access-icon" aria-hidden="true">📄</span>
          <div>
            <h1 className="form-access-title">Access Your 1095 Form Securely Online</h1>
            <p className="form-access-subtitle">
              Please enter the information below to retrieve your most recent IRS Form 1095.
            </p>
            <p className="form-access-subtitle">
              Once your information is verified, your form will automatically download. 
            </p>
            <p className="form-access-subtitle">
              You may also choose to have a copy sent to an email address. If you choose email delivery, please ensure the email address is entered correctly.
            </p>
            {/* <p className="form-access-subtitle">If you choose email delivery, please ensure the email address is entered correctly.
            </p> */}
            <p className="form-access-subtitle">For your security, all forms are password protected using your last name in lowercase with no spaces or punctuation, followed by your 5 digit ZIP code (for example: O'Brien + 12345 = obrien12345;  McDonald + 10010-1524 = mcdonald10010; Van Buren + 08540=vanburen08540; Smith-Jones + 90033 = smithjones90033).
           
            </p>
            <p className="form-access-subtitle">If you have any issues accessing your form or believe there is an error, please contact your HR department.
            </p>
          </div>
        </div>

        {/* ── Info Banner ─────────────────────────────────────────────── */}
       {/*  <div className="form-access-info-banner">
          ⓘ Your information is used to verify your identity and generate the correct 1095 form.
        </div> */}

        {/* ── Loading Banner ──────────────────────────────────────────── */}
        {form.loading && (
          <AlertBanner
            type="loading"
            message="We are validating your input, please wait…"
          />
        )}

        {/* ── Error Messages ──────────────────────────────────────────── */}
        {!form.loading && form.result?.error && (
          <AlertBanner
            type="error"
            message={form.result.error.userMessage}
          />
        )}

        {/* ── Success Message ─────────────────────────────────────────── */}
        {form.result?.success && !form.result.downloaded && (
          <AlertBanner
            type="success"
            message="Form access validated. Generating your 1095 form…"
          />
        )}
        {showDownloadBanner && (
          <AlertBanner
            type="success"
            message={` File downloaded successfully${form.result?.fileName ? ` — ${form.result.fileName}` : ''}.`}
          />
        )}

        {/* ── Form Fields ─────────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} noValidate className="form-access-form">
          <p className="form-access-required-note">
            Fields marked with <strong>*</strong> are required.
          </p>

          {/* First Name */}
          <FormField
            id="firstName"
            label="First Name"
            required={true}
            value={form.firstName}
            onChange={form.setFirstName}
            onBlur={() => form.handleFieldBlur('firstName')}
            error={form.fieldErrors.firstName}
            placeholder="Enter your first name"
            maxLength={60}
            disabled={form.loading}
          />

          {/* Last Name */}
          <FormField
            id="lastName"
            label="Last Name"
            required={true}
            value={form.lastName}
            onChange={form.setLastName}
            onBlur={() => form.handleFieldBlur('lastName')}
            error={form.fieldErrors.lastName}
            placeholder="Enter your last name"
            maxLength={60}
            disabled={form.loading}
          />

          {/* SSN Last 4 Digits */}
          <FormField
            id="ssn4"
            label="Last 4 Digits of SSN"
            required={true}
            value={form.ssn4}
            onChange={handleSsn4Change}
            onBlur={() => form.handleFieldBlur('ssn4')}
            error={form.fieldErrors.ssn4}
            type={form.showSSN ? 'text' : 'password'}
            placeholder="Enter 4 digits"
            maxLength={4}
            disabled={form.loading}
            suffix={
              <button
                type="button"
                className="form-access-ssn-toggle"
                onClick={() => form.setShowSSN(v => !v)}
                aria-label={form.showSSN ? 'Hide SSN' : 'Show SSN'}
                disabled={form.loading}
                title={form.showSSN ? 'Hide SSN' : 'Show SSN'}
              >
                <img
                  src={form.showSSN ? pwdHide : pwdVisible}
                  alt={form.showSSN ? 'Hide SSN' : 'Show SSN'}
                  className="form-access-ssn-icon"
                />
              </button>
            }
          />

       


          {/* Email */}
          <FormField
            id="email"
            label="Email Address"
            required={false}
            value={form.email}
            onChange={form.setEmail}
            onBlur={() => form.handleFieldBlur('email')}
            error={form.fieldErrors.email}
            type="email"
            placeholder="Enter your email (optional)"
            disabled={form.loading}
          />
          <p className="form-access-email-hint">
            💡 We'll send your form to this email address.
          </p>

          {/* Email Verification / Re-entry */}
          {form.email && (
            <FormField
              id="emailVerify"
              label="Confirm Email Address"
              required={true}
              value={form.emailVerify}
              onChange={form.setEmailVerify}
              onBlur={() => form.handleFieldBlur('emailVerify')}
              error={form.fieldErrors.emailVerify}
              type="email"
              placeholder="Re-enter your email to confirm"
              disabled={form.loading}
            />
          )}

          {/* Download Consent */}
          <label className="form-access-consent">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={e => setConsentChecked(e.target.checked)}
              disabled={form.loading}
            />
            <span>
              I understand that my form will automatically download to this device based on my browser settings.
              If I am using a shared or public computer, I am responsible for protecting my personal information
              and securely closing or removing downloaded files after use.
            </span>
          </label>

          {/* Action Buttons */}
          <div className="form-access-actions">
            <button
              type="button"
              className="form-access-btn form-access-btn--cancel"
              onClick={() => navigate('/verify')}
              disabled={form.loading}
            >
              Back
            </button>
            <button
              type="submit"
              className="form-access-btn form-access-btn--submit"
              disabled={form.loading || !consentChecked}
            >
              {form.loading
                ? '🔄 Verifying your form...'
                : 'Generate Form ▶'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
